import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import api, { fmt, fmtInt, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Search, Save, Building2, User, Hash, AlertTriangle, ArrowUpToLine, Sparkles, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import CategoryCombobox from "@/components/CategoryCombobox";
import PoImportButton from "@/components/PoImportButton";

const emptyBuyer = { name: "", address: "", gstin: "", state: "", state_code: "", contact: "" };
const emptyLine = { item_id: "", name: "", hsn: "", category: "Uncategorized", unit: "Pack", qty: 1, rate: 0, tax_percent: 0, mrp: 0 };

const priceFor = (item, type) =>
    type === "B2C"
        ? (Number(item.b2c_price) || Number(item.our_price) || Number(item.b2b_price) || 0)
        : (Number(item.b2b_price) || Number(item.our_price) || Number(item.b2c_price) || 0);

export default function NewInvoice() {
    const nav = useNavigate();
    const [sp] = useSearchParams();
    const { id: editId } = useParams();
    const isEditing = !!editId;
    const prefName = sp.get("name") || "";
    const prefGstin = sp.get("gstin") || "";

    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
    const itemsByName = useMemo(() => Object.fromEntries(items.map((i) => [(i.name || "").trim().toLowerCase(), i])), [items]);
    // Today's item-master rate for a line (by id, then name) — drives the ↑/↓ hint.
    const masterRateOf = (l) => {
        const m = itemsById[l.item_id] || itemsByName[(l.name || "").trim().toLowerCase()];
        return m ? Number(priceFor(m, invoiceType)) || 0 : null;
    };

    // Edit mode: skip chooser and load the invoice.
    // Clients-page prefill: also skip chooser (auto-fill effect populates type from history).
    const [step, setStep] = useState(isEditing || prefName || prefGstin ? "build" : "choose-type");
    const [invoiceType, setInvoiceType] = useState("B2B");
    const [typeSource, setTypeSource] = useState("default");

    const [buyer, setBuyer] = useState({ ...emptyBuyer, name: prefName, gstin: prefGstin });
    const [lines, setLines] = useState([{ ...emptyLine }]);
    const [delivery, setDelivery] = useState(0);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [pickerFor, setPickerFor] = useState(null);
    const [pickerQ, setPickerQ] = useState("");
    const [recentBuyers, setRecentBuyers] = useState([]);
    const [showBuyerPicker, setShowBuyerPicker] = useState(false);
    const [invoiceNumberOverride, setInvoiceNumberOverride] = useState("");
    const [showNumberOverride, setShowNumberOverride] = useState(false);
    const [previousSavings, setPreviousSavings] = useState("");
    const [previousSavingsAuto, setPreviousSavingsAuto] = useState(0);
    const [invoiceDate, setInvoiceDate] = useState("");
    const savingsDebounce = useRef();
    const [loading, setLoading] = useState(isEditing);
    const nameDebounce = useRef();

    useEffect(() => {
        api.get("/items", { params: { active_only: true } }).then((r) => setItems(r.data));
        api.get("/items/categories").then((r) => setCats(r.data)).catch(() => {});
        if (!isEditing) {
            api.get("/buyers/recent").then((r) => setRecentBuyers(r.data)).catch(() => {});
            api.get("/buyers/suggest-type").then((r) => {
                setInvoiceType(r.data.invoice_type);
                setTypeSource(r.data.source);
            }).catch(() => {});
        }
    }, [isEditing]);

    // Prefill from a client-portal order (P4). ClientOrdersPage stashes the order
    // in sessionStorage and navigates here with ?fromClientOrder=<id>. We hydrate
    // buyer + lines from it, and remember the source order id so we can call
    // /admin/client-orders/{id}/mark-converted after the invoice is saved.
    //
    // Auto-type rule (per HDB): if the buyer already has invoice history the
    // B2B/B2C chooser step is SKIPPED — the type comes from their last invoice.
    // Brand-new buyers still see the chooser so staff can decide explicitly.
    //
    // Rate rule: each line's rate is overridden with the rate this client paid
    // on their MOST RECENT invoice containing that item (client history always
    // wins over master price).
    const [sourceClientOrderId, setSourceClientOrderId] = useState("");
    useEffect(() => {
        if (isEditing) return;
        const orderId = sp.get("fromClientOrder");
        if (!orderId) return;
        const raw = sessionStorage.getItem("hdb-prefill-order");
        if (!raw) return;
        (async () => {
            try {
                const p = JSON.parse(raw);
                if (p.client_order_id !== orderId) return;
                setSourceClientOrderId(orderId);
                setBuyer((b) => ({ ...b, name: p.name || "" }));
                setInvoiceType(p.invoice_type || "B2B");
                let prefLines = (p.line_items || []).map((l) => ({
                    item_id: l.item_id || "",
                    name: l.name || "",
                    hsn: l.hsn || "",
                    category: l.category || "Uncategorized",
                    unit: l.unit || "Pack",
                    qty: l.qty || 0,
                    rate: l.rate || 0,
                    tax_percent: l.tax_percent || 0,
                    mrp: l.mrp || l.rate || 0,
                }));

                // Override each line's rate with this client's most-recent
                // invoiced rate for the same item (client history wins) —
                // except reorders, which keep the source invoice's rates.
                if (!p.reorder_of_invoice_id) try {
                    const lr = await api.get(`/clients/${encodeURIComponent(p.name)}/last-rates`);
                    const rates = lr.data?.rates || {};
                    let overridden = 0;
                    prefLines = prefLines.map((l) => {
                        const hit = rates[l.item_id] || rates[(l.name || "").trim().toLowerCase()];
                        if (hit && hit.rate > 0) { overridden++; return { ...l, rate: hit.rate }; }
                        return l;
                    });
                    if (overridden) {
                        toast.info(`${overridden} rate${overridden > 1 ? "s" : ""} taken from this client's last invoice`);
                    }
                } catch { /* last-rates is best-effort */ }

                if (prefLines.length) setLines(prefLines);

                // Existing client (has invoice history) → skip the type chooser.
                // New client → land on the chooser so staff picks B2B/B2C.
                try {
                    const st = await api.get("/buyers/suggest-type",
                                             { params: { name: p.name || "" } });
                    if (st.data?.source === "buyer_history") {
                        setInvoiceType(st.data.invoice_type || p.invoice_type || "B2B");
                        setTypeSource("buyer_history");
                        setStep("build");
                        toast.success(`Prefilled from client order · ${prefLines.length} items · type auto-set to ${st.data.invoice_type}`);
                    } else {
                        toast.success(`Prefilled from client order · ${prefLines.length} items · pick B2B or B2C for this new client`);
                    }
                } catch {
                    setStep("build");
                    toast.success(`Prefilled from client order · ${prefLines.length} items`);
                }
                sessionStorage.removeItem("hdb-prefill-order");
            } catch (e) { /* ignore malformed prefill */ }
        })();
        /* eslint-disable-next-line */
    }, [isEditing]);

    // Edit mode: hydrate the form from the saved invoice.
    useEffect(() => {
        if (!isEditing) return;
        api.get(`/invoices/${editId}`).then((r) => {
            const inv = r.data;
            setBuyer({
                name: inv.buyer.name || "",
                address: inv.buyer.address || "",
                gstin: inv.buyer.gstin || "",
                state: inv.buyer.state || "",
                state_code: inv.buyer.state_code || "",
                contact: inv.buyer.contact || "",
            });
            setLines(inv.line_items.map((l) => ({
                item_id: l.item_id || "",
                name: l.name,
                hsn: l.hsn || "",
                category: l.category || "Uncategorized",
                unit: l.unit || "Pack",
                qty: l.qty,
                rate: l.rate,
                tax_percent: l.tax_percent,
                mrp: l.mrp || 0,
            })));
            setDelivery(inv.delivery || 0);
            setInvoiceType(inv.invoice_type || "B2B");
            setInvoiceNumberOverride(inv.invoice_number || "");
            setInvoiceDate(inv.invoice_date || "");
            setShowNumberOverride(true);
            setLoading(false);
        }).catch(() => {
            toast.error("Could not load invoice");
            setLoading(false);
        });
    }, [isEditing, editId]);

    // When buyer name changes, look up whether they were previously a B2B or B2C customer
    // and auto-fill their address / GSTIN / state / state_code / contact from history.
    useEffect(() => {
        if (isEditing) return; // Don't auto-modify while editing an existing invoice
        if (!buyer.name && !buyer.gstin) return;
        clearTimeout(nameDebounce.current);
        nameDebounce.current = setTimeout(() => {
            api.get("/buyers/suggest-type", { params: { name: buyer.name, gstin: buyer.gstin } })
                .then((r) => {
                    if (r.data.source !== "buyer_history") {
                        setTypeSource(r.data.source);
                        return;
                    }
                    // Auto-fill blank buyer fields from history (don't clobber what user typed)
                    if (r.data.buyer) {
                        setBuyer((b) => ({
                            ...b,
                            address: b.address || r.data.buyer.address || "",
                            gstin: b.gstin || r.data.buyer.gstin || "",
                            state: b.state || r.data.buyer.state || "",
                            state_code: b.state_code || r.data.buyer.state_code || "",
                            contact: b.contact || r.data.buyer.contact || "",
                        }));
                    }
                    if (r.data.invoice_type !== invoiceType) {
                        toast.info(`This buyer was last invoiced as ${r.data.invoice_type} — switched automatically.`);
                        setInvoiceType(r.data.invoice_type);
                        // Reprice existing item-lines from item master
                        setLines((cur) => cur.map((l) => {
                            if (l.item_id && itemsById[l.item_id]) {
                                return { ...l, rate: priceFor(itemsById[l.item_id], r.data.invoice_type) };
                            }
                            return l;
                        }));
                    }
                    setTypeSource("buyer_history");
                }).catch(() => {});
        }, 400);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyer.name, buyer.gstin]);

    const setLine = (i, patch) => setLines(lines.map((l, k) => k === i ? { ...l, ...patch } : l));
    const addLine = () => setLines([...lines, { ...emptyLine }]);
    const rmLine = (i) => setLines(lines.filter((_, k) => k !== i));

    const chooseItem = (i, item) => {
        setLine(i, {
            item_id: item.id, name: item.name, hsn: item.hsn, category: item.category,
            unit: item.unit, rate: priceFor(item, invoiceType), tax_percent: item.tax_percent,
            mrp: item.mrp || 0,
        });
        setPickerFor(null);
        setPickerQ("");
    };

    // Add a master item as a NEW line (for the global search bar). Replaces the first
    // empty placeholder row if present so a blank invoice doesn't accumulate empty lines.
    const addItemToInvoice = (item) => {
        const asLine = {
            item_id: item.id, name: item.name, hsn: item.hsn, category: item.category,
            unit: item.unit, qty: 1, rate: priceFor(item, invoiceType), tax_percent: item.tax_percent,
            mrp: item.mrp || 0,
        };
        setLines((prev) => {
            const isPlaceholder = prev.length === 1 && !prev[0].name && !Number(prev[0].qty);
            return isPlaceholder ? [asLine] : [...prev, asLine];
        });
        toast.success(`Added: ${item.name}`);
    };

    // --------- Global "Verify HSN & GST" ---------
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null); // { suggestions, checked, total_changes }
    const [selectedFixes, setSelectedFixes] = useState({});  // "index-field" -> bool

    const runVerify = async () => {
        const nonEmpty = lines
            .map((l, idx) => ({ l, idx }))
            .filter(({ l }) => l.name && l.name.trim());
        if (nonEmpty.length === 0) return toast.error("Add some line items first");
        setVerifying(true);
        try {
            const payload = {
                lines: nonEmpty.map(({ l }) => ({
                    name: l.name, hsn: l.hsn || "",
                    tax_percent: l.tax_percent !== "" ? Number(l.tax_percent) : null,
                })),
            };
            // Map from packed idx → original line idx
            const idxMap = nonEmpty.map(({ idx }) => idx);
            const r = await api.post("/ai/verify-invoice-lines", payload, { timeout: 120000 });
            const sugs = (r.data.suggestions || []).map((s) => ({ ...s, line_idx: idxMap[s.index] }));
            const pre = {};
            sugs.forEach((s) => (s.changes || []).forEach((c) => { pre[`${s.line_idx}-${c.field}`] = true; }));
            setSelectedFixes(pre);
            setVerifyResult({ ...r.data, suggestions: sugs });
            if (r.data.total_changes === 0) toast.success("All HSN & GST values look correct ✓");
            else toast(`${r.data.total_changes} suggestion${r.data.total_changes > 1 ? "s" : ""} — review below`, { icon: "✨" });
        } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
        setVerifying(false);
    };

    const applyFixes = () => {
        if (!verifyResult) return;
        let applied = 0;
        const updated = [...lines];
        for (const s of verifyResult.suggestions) {
            for (const c of s.changes || []) {
                if (!selectedFixes[`${s.line_idx}-${c.field}`]) continue;
                if (c.field === "hsn") updated[s.line_idx] = { ...updated[s.line_idx], hsn: c.suggested, hsn_auto: true };
                else if (c.field === "tax_percent") updated[s.line_idx] = { ...updated[s.line_idx], tax_percent: c.suggested };
                applied++;
            }
        }
        setLines(updated);
        setVerifyResult(null);
        toast.success(`Applied ${applied} fix${applied !== 1 ? "es" : ""}`);
    };

    const pickBuyer = (b) => {
        setBuyer({ ...emptyBuyer, ...b.buyer });
        setInvoiceType(b.invoice_type);
        setTypeSource("buyer_history");
        setShowBuyerPicker(false);
        setLines((cur) => cur.map((l) => {
            if (l.item_id && itemsById[l.item_id]) {
                return { ...l, rate: priceFor(itemsById[l.item_id], b.invoice_type) };
            }
            return l;
        }));
    };

    const switchType = (t) => {
        if (t === invoiceType) return;
        setInvoiceType(t);
        setTypeSource("manual");
        setLines((cur) => cur.map((l) => {
            if (l.item_id && itemsById[l.item_id]) {
                return { ...l, rate: priceFor(itemsById[l.item_id], t) };
            }
            return l;
        }));
        toast.success(`Switched to ${t} pricing — line items re-priced.`);
    };

    const filteredItems = useMemo(() => {
        if (!pickerQ) return items.slice(0, 30);
        const q = pickerQ.toLowerCase();
        return items.filter((it) => it.name.toLowerCase().includes(q) || it.hsn.includes(q)).slice(0, 30);
    }, [items, pickerQ]);

    // Union of item-master categories + categories already in the current invoice lines.
    const allCats = useMemo(() => {
        const seen = new Set(cats.filter(Boolean));
        for (const l of lines) if (l.category) seen.add(l.category);
        return Array.from(seen).sort((a, b) => a.localeCompare(b));
    }, [cats, lines]);

    // Auto-sort lines by category: preserves each line's original position within its category
    // group so newly-added items visually snap under the correct heading — same order as the
    // grouped PDF.
    const sortedIndices = useMemo(() => {
        const groups = new Map();
        lines.forEach((l, idx) => {
            const key = l.category || "Uncategorized";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(idx);
        });
        return Array.from(groups.values()).flat();
    }, [lines]);

    // Duplicate-detection: fuzzy match against every OTHER line in the current invoice.
    const duplicateHints = useMemo(() => {
        const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
        const out = new Map(); // index -> hint string
        for (let i = 0; i < lines.length; i++) {
            const a = lines[i];
            if (!a.name || !a.name.trim()) continue;
            const na = norm(a.name);
            for (let j = 0; j < lines.length; j++) {
                if (i === j) continue;
                const b = lines[j];
                if (!b.name) continue;
                const nb = norm(b.name);
                const exactName = na === nb && na.length > 0;
                const sameHsn = a.hsn && b.hsn && a.hsn === b.hsn;
                const nameOverlap = na.length >= 6 && nb.length >= 6 &&
                    (na.includes(nb) || nb.includes(na) ||
                     na.substring(0, 8) === nb.substring(0, 8));
                if (exactName || (sameHsn && nameOverlap)) {
                    out.set(i, exactName
                        ? `Row #${j + 1} has the exact same item name.`
                        : `Row #${j + 1} has same HSN and similar name.`);
                    break;
                }
            }
        }
        return out;
    }, [lines]);

    const [hsnBusy, setHsnBusy] = useState(new Set());
    const [rateBusy, setRateBusy] = useState(new Set());

    // Global "Quick add from Item Master" search state
    const [globalSearch, setGlobalSearch] = useState("");
    const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
    const globalSearchResults = useMemo(() => {
        const q = globalSearch.trim().toLowerCase();
        if (!q) return [];
        return items
            .filter((it) => it.name.toLowerCase().includes(q) || (it.hsn || "").includes(q))
            .slice(0, 20);
    }, [items, globalSearch]);

    const autoFetchHsn = async (i) => {
        const l = lines[i];
        if (!l.name || !l.name.trim()) return toast.error("Enter the item name first");
        setHsnBusy((s) => new Set([...s, i]));
        try {
            const r = await api.post("/ai/lookup-hsn-gst", { name: l.name });
            const d = r.data || {};
            const patch = {};
            if (d.hsn) patch.hsn = d.hsn;
            if (d.gst_rate != null) patch.tax_percent = d.gst_rate;
            if (Object.keys(patch).length === 0) {
                toast.warning(`Could not confidently lookup HSN — ${d.note || "no match"}`);
            } else {
                setLine(i, { ...patch, hsn_auto: true });
                toast.success(`HSN ${d.hsn || "?"} · GST ${d.gst_rate ?? "?"}% (${d.confidence}% confidence) — please verify`);
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
        setHsnBusy((s) => { const n = new Set(s); n.delete(i); return n; });
    };

    const updateMasterRate = async (i) => {
        const l = lines[i];
        if (!l.item_id) return toast.error("Pick an item from the master first");
        const rate = Number(l.rate || 0);
        if (rate <= 0) return toast.error("Enter a rate > 0");
        const master = itemsById[l.item_id];
        const currentMasterRate = invoiceType === "B2C" ? master?.b2c_price : master?.b2b_price;
        if (Math.abs((currentMasterRate || 0) - rate) < 0.001) {
            return toast.info(`Master already at ₹${rate} for ${invoiceType}`);
        }
        if (!window.confirm(
            `Update Item Master rate for "${l.name}"?\n\n` +
            `${invoiceType} price will change from ₹${currentMasterRate || 0} → ₹${rate}.\n` +
            `Future invoices will use the new rate.`
        )) return;
        setRateBusy((s) => new Set([...s, i]));
        try {
            const body = invoiceType === "B2C" ? { b2c_price: rate } : { b2b_price: rate };
            const r = await api.patch(`/items/${l.item_id}/price`, body);
            if (r.data.unchanged) {
                toast.info("Rate already up to date");
            } else {
                toast.success(`Master updated · ₹${rate} for ${invoiceType}`);
                // refresh items so subsequent picks reflect the new master price
                api.get("/items", { params: { active_only: true } }).then((rr) => setItems(rr.data));
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        setRateBusy((s) => { const n = new Set(s); n.delete(i); return n; });
    };

    // Auto-fetch cumulative savings for a returning B2C buyer.
    useEffect(() => {
        if (invoiceType !== "B2C") return;
        if (!buyer.name && !buyer.contact) { setPreviousSavingsAuto(0); return; }
        clearTimeout(savingsDebounce.current);
        savingsDebounce.current = setTimeout(() => {
            api.get("/buyers/previous-savings", { params: { name: buyer.name, contact: buyer.contact, gstin: buyer.gstin } })
                .then((r) => {
                    setPreviousSavingsAuto(r.data.previous_savings || 0);
                    if (previousSavings === "") setPreviousSavings(String(r.data.previous_savings || 0));
                }).catch(() => {});
        }, 400);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyer.name, buyer.contact, invoiceType]);

    const payload = () => ({
        buyer,
        line_items: lines.filter((l) => l.name && l.qty > 0).map((l) => ({
            item_id: l.item_id || null,
            name: l.name, hsn: l.hsn, category: l.category, unit: l.unit,
            qty: Number(l.qty), rate: Number(l.rate), tax_percent: Number(l.tax_percent),
            mrp: Number(l.mrp || 0),
        })),
        delivery: Number(delivery || 0),
        invoice_type: invoiceType,
        invoice_number: invoiceNumberOverride.trim() || undefined,
        invoice_date: invoiceDate || undefined,
        previous_savings_override: (invoiceType === "B2C" && previousSavings !== "" && !isNaN(Number(previousSavings)))
            ? Number(previousSavings) : undefined,
    });

    useEffect(() => {
        const p = payload();
        if (p.line_items.length === 0) { setPreview(null); return; }
        const t = setTimeout(() => {
            api.post("/invoices/preview", p).then((r) => setPreview(r.data)).catch(() => {});
        }, 200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(lines), delivery, buyer.state_code, buyer.name, buyer.contact, buyer.gstin, invoiceType, previousSavings]);

    const save = async () => {
        if (!buyer.name) return toast.error("Buyer name is required");
        const p = payload();
        if (p.line_items.length === 0) return toast.error("Add at least one line item");
        setSaving(true);
        try {
            if (isEditing) {
                const r = await api.put(`/invoices/${editId}`, p);
                toast.success(`Invoice ${r.data.invoice_number} updated`);
                nav(`/invoices/${editId}`);
            } else {
                const r = await api.post("/invoices", p);
                toast.success(`Invoice ${r.data.invoice_number} created (${invoiceType})`);
                // If this invoice was created from a client-portal order,
                // mark that order as converted so it disappears from the inbox.
                if (sourceClientOrderId) {
                    api.post(`/admin/client-orders/${sourceClientOrderId}/mark-converted`,
                             { invoice_id: r.data.id }).catch(() => {});
                }
                nav(`/invoices/${r.data.id}`);
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
        setSaving(false);
    };

    if (loading) return <div data-testid="edit-loading" className="text-slate-500">Loading invoice…</div>;

    // ---------- Step 1: Choose Invoice Type ----------
    if (step === "choose-type") {
        return (
            <div data-testid="type-chooser" className="max-w-3xl mx-auto pt-6">
                <h1 className="text-3xl hdb-heading font-black text-slate-900 mb-1">Create Invoice</h1>
                <p className="text-slate-500 text-sm mb-8">Choose the pricing bracket for this invoice.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <button
                        data-testid="choose-B2B"
                        onClick={() => { setInvoiceType("B2B"); setStep("build"); }}
                        className={`hdb-card p-8 text-left hover:border-slate-900 transition-colors ${invoiceType === "B2B" ? "ring-2 ring-slate-900" : ""}`}>
                        <Building2 size={28} className="text-slate-700 mb-3" />
                        <div className="hdb-heading font-black text-xl text-slate-900">B2B</div>
                        <div className="text-sm text-slate-500 mt-1">Business / wholesale pricing — for kirana stores, HORECA and re-sellers.</div>
                        {typeSource === "global_default" && invoiceType === "B2B" && (
                            <div className="mt-3 text-xs text-emerald-700 font-semibold">Last used pricing</div>
                        )}
                    </button>
                    <button
                        data-testid="choose-B2C"
                        onClick={() => { setInvoiceType("B2C"); setStep("build"); }}
                        className={`hdb-card p-8 text-left hover:border-slate-900 transition-colors ${invoiceType === "B2C" ? "ring-2 ring-slate-900" : ""}`}>
                        <User size={28} className="text-emerald-700 mb-3" />
                        <div className="hdb-heading font-black text-xl text-slate-900">B2C</div>
                        <div className="text-sm text-slate-500 mt-1">Retail pricing — for individual end customers.</div>
                        {typeSource === "global_default" && invoiceType === "B2C" && (
                            <div className="mt-3 text-xs text-emerald-700 font-semibold">Last used pricing</div>
                        )}
                    </button>
                </div>
                <p className="text-xs text-slate-400 mt-6">
                    You can switch anytime after starting. HDB will remember each buyer's pricing bracket for the next invoice.
                </p>
            </div>
        );
    }

    // ---------- Step 2: Build the invoice ----------
    return (
        <div data-testid="new-invoice-page">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">
                        {isEditing ? "Edit Invoice" : "Create Invoice"}
                    </h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        {isEditing
                            ? "Changes recompute all totals · Invoice number editable"
                            : "Number is auto-generated on save · Financial year based"}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5" data-testid="type-toggle">
                        {["B2B", "B2C"].map((t) => (
                            <button key={t} onClick={() => switchType(t)}
                                    data-testid={`switch-${t}`}
                                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                        invoiceType === t ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                                    }`}>
                                {t}
                            </button>
                        ))}
                    </div>
                    {isEditing && (
                        <button className="hdb-btn hdb-btn-ghost" onClick={() => nav(`/invoices/${editId}`)}
                                data-testid="cancel-edit-btn">
                            Cancel
                        </button>
                    )}
                    <button className="hdb-btn hdb-btn-mint" onClick={save} disabled={saving} data-testid="save-invoice-btn">
                        <Save size={14} /> {saving ? "Saving…" : (isEditing ? "Save Changes" : "Save Invoice")}
                    </button>
                </div>
            </div>

            {/* Invoice number + date controls */}
            <div className="hdb-card p-4 mb-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => setShowNumberOverride(!showNumberOverride)}
                        className="flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900"
                        data-testid="toggle-number-override">
                        <Hash size={14} className="text-slate-500" />
                        <span className="font-semibold">Invoice number</span>
                        <span className="text-xs text-slate-500">
                            {showNumberOverride
                                ? "— manual override (leave blank to auto-generate)"
                                : "— auto-generated (click to override)"}
                        </span>
                    </button>
                </div>
                {showNumberOverride && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                        <div>
                            <label className="hdb-label">Invoice # (optional)</label>
                            <input className="hdb-input hdb-mono" value={invoiceNumberOverride}
                                   onChange={(e) => setInvoiceNumberOverride(e.target.value)}
                                   placeholder="e.g. HBEPL/26-27/0050"
                                   data-testid="invoice-number-input" />
                            <div className="text-[11px] text-slate-500 mt-1">
                                {isEditing
                                    ? "Change only if you need to correct a wrongly-assigned number."
                                    : "Leave blank to auto-continue the running sequence."}
                            </div>
                        </div>
                        <div>
                            <label className="hdb-label">Invoice date {isEditing ? "" : "(optional)"}</label>
                            <input className="hdb-input hdb-mono" type="date" value={invoiceDate}
                                   onChange={(e) => setInvoiceDate(e.target.value)}
                                   data-testid="invoice-date-input" />
                            <div className="text-[11px] text-slate-500 mt-1">
                                Defaults to today if left blank.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {typeSource === "buyer_history" && (
                <div className="mb-4 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-800 flex items-center gap-2" data-testid="memory-banner">
                    <span className="font-semibold">Smart memory:</span> Returning buyer — {invoiceType} pricing and previous contact details were auto-filled from your last invoice for them.
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    {/* Buyer */}
                    <div className="hdb-card p-5 relative">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="hdb-heading font-bold text-lg text-slate-900">Bill To</h3>
                            {recentBuyers.length > 0 && (
                                <button onClick={() => setShowBuyerPicker(!showBuyerPicker)}
                                        className="hdb-btn hdb-btn-ghost text-xs" data-testid="recent-buyers-btn">
                                    Recent buyers
                                </button>
                            )}
                        </div>
                        {showBuyerPicker && (
                            <div className="absolute right-5 top-14 z-40 bg-white border border-slate-200 rounded-md shadow-lg w-80 max-h-64 overflow-y-auto"
                                 data-testid="recent-buyers-list">
                                {recentBuyers.map((b, i) => (
                                    <button key={i} onClick={() => pickBuyer(b)}
                                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0 text-sm">
                                        <div className="font-medium">{b.buyer.name}</div>
                                        <div className="text-xs text-slate-500 flex gap-3">
                                            <span className={`hdb-tag ${b.invoice_type === "B2C" ? "hdb-tag-gold" : "hdb-tag-mint"} !py-0`}>
                                                {b.invoice_type}
                                            </span>
                                            {b.buyer.gstin && <span className="hdb-mono">{b.buyer.gstin}</span>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="hdb-label">Buyer Name *</label>
                                <input className="hdb-input" value={buyer.name}
                                       onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                                       data-testid="buyer-name" />
                            </div>
                            <div className="col-span-2">
                                <label className="hdb-label">Address</label>
                                <input className="hdb-input" value={buyer.address}
                                       onChange={(e) => setBuyer({ ...buyer, address: e.target.value })}
                                       data-testid="buyer-address" />
                            </div>
                            <div>
                                <label className="hdb-label">GSTIN {invoiceType === "B2C" && <span className="text-slate-400">(optional)</span>}</label>
                                <input className="hdb-input hdb-mono" value={buyer.gstin}
                                       onChange={(e) => setBuyer({ ...buyer, gstin: e.target.value.toUpperCase() })}
                                       data-testid="buyer-gstin" />
                            </div>
                            <div>
                                <label className="hdb-label">Contact</label>
                                <input className="hdb-input" value={buyer.contact}
                                       onChange={(e) => setBuyer({ ...buyer, contact: e.target.value })} />
                            </div>
                            <div>
                                <label className="hdb-label">State</label>
                                <input className="hdb-input" value={buyer.state}
                                       onChange={(e) => setBuyer({ ...buyer, state: e.target.value })}
                                       data-testid="buyer-state" />
                            </div>
                            <div>
                                <label className="hdb-label">State Code (2 digits)</label>
                                <input className="hdb-input hdb-mono" value={buyer.state_code}
                                       onChange={(e) => setBuyer({ ...buyer, state_code: e.target.value })}
                                       data-testid="buyer-state-code" />
                            </div>
                        </div>
                        {buyer.state_code && preview && (
                            <div className="mt-3 text-xs">
                                <span className={`hdb-tag ${preview.is_interstate ? "hdb-tag-gold" : "hdb-tag-mint"}`}>
                                    {preview.is_interstate ? "Inter-State · IGST" : "Intra-State · CGST + SGST"}
                                </span>
                                <span className={`hdb-tag ml-2 ${invoiceType === "B2C" ? "hdb-tag-gold" : "hdb-tag-mint"}`}>
                                    {invoiceType} Pricing
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Line Items */}
                    <div className="hdb-card overflow-visible">
                        <div className="flex justify-between items-center p-5 border-b border-slate-100">
                            <h3 className="hdb-heading font-bold text-lg text-slate-900">Line Items</h3>
                            <div className="flex gap-2 flex-wrap">
                                <PoImportButton
                                    invoiceType={invoiceType}
                                    testId="ni-po-import"
                                    onItemsExtracted={(items, meta) => {
                                        // Append parsed line items to whatever's already on the invoice.
                                        // Buyer info and invoice_type are unchanged — this is the
                                        // "existing client, still want PO items" flow.
                                        setLines((prev) => {
                                            // Drop the first empty placeholder row if present.
                                            const trimmed = prev.length === 1 && !prev[0].name && !Number(prev[0].qty)
                                                ? []
                                                : prev;
                                            return [...trimmed, ...items.map((l) => ({
                                                item_id: l.item_id || null,
                                                name: l.name || "",
                                                hsn: l.hsn || "",
                                                category: l.category || "Uncategorized",
                                                unit: l.unit || "Pack",
                                                qty: Number(l.qty || 1),
                                                rate: Number(l.rate || 0),
                                                tax_percent: Number(l.tax_percent || 0),
                                                mrp: 0,
                                            }))];
                                        });
                                        if (meta.unmatched > 0) {
                                            toast.warning(`${meta.unmatched} item${meta.unmatched > 1 ? "s" : ""} need rate — highlighted in the list`);
                                        }
                                    }}
                                />
                                <button className="hdb-btn hdb-btn-ghost" onClick={addLine} data-testid="add-line-btn">
                                    <Plus size={13} /> Add Row
                                </button>
                                <button className="hdb-btn hdb-btn-mint"
                                        onClick={runVerify} disabled={verifying}
                                        title="Ask AI to check every line's HSN and GST rate"
                                        data-testid="verify-hsn-gst-btn">
                                    {verifying
                                        ? <><Loader2 size={13} className="animate-spin" /> Verifying…</>
                                        : <><ShieldCheck size={13} /> Verify HSN &amp; GST</>}
                                </button>
                            </div>
                        </div>

                        {/* Global add-item search bar (helpful when editing a large bill) */}
                        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 relative">
                            <label className="hdb-label mb-1 flex items-center gap-1">
                                <Search size={11} /> Quick add from Item Master
                            </label>
                            <input className="hdb-input"
                                   placeholder="Search master by name or HSN, then click to add…"
                                   value={globalSearch}
                                   onFocus={() => setGlobalSearchOpen(true)}
                                   onChange={(e) => { setGlobalSearch(e.target.value); setGlobalSearchOpen(true); }}
                                   data-testid="global-item-search" />
                            {globalSearchOpen && globalSearch.trim() && (
                                <div className="absolute left-5 right-5 z-30 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto"
                                     data-testid="global-search-results">
                                    {globalSearchResults.length === 0 && (
                                        <div className="px-3 py-3 text-sm text-slate-400 text-center">No matches</div>
                                    )}
                                    {globalSearchResults.map((it) => (
                                        <button key={it.id} type="button" onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => { addItemToInvoice(it); setGlobalSearch(""); setGlobalSearchOpen(false); }}
                                                className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-50 last:border-0"
                                                data-testid={`global-add-${it.id}`}>
                                            <div className="font-medium text-sm">{it.name}</div>
                                            <div className="text-xs text-slate-500 flex gap-3 hdb-mono">
                                                <span>HSN {it.hsn}</span>
                                                <span className="font-bold text-slate-900">₹{fmtNum(priceFor(it, invoiceType))}</span>
                                                <span>{it.tax_percent}%</span>
                                                <span>{it.category}</span>
                                            </div>
                                        </button>
                                    ))}
                                    <div className="p-2 border-t border-slate-100 text-right">
                                        <button type="button" className="hdb-btn hdb-btn-ghost text-xs"
                                                onClick={() => setGlobalSearchOpen(false)}>Close</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Verify HSN & GST results panel */}
                        {verifyResult && (
                            <div className="px-5 py-4 border-b border-slate-100 bg-amber-50/50" data-testid="verify-panel">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <div className="hdb-heading font-bold text-sm text-slate-900">
                                            AI verification · {verifyResult.checked} line{verifyResult.checked !== 1 ? "s" : ""} checked
                                        </div>
                                        <div className="text-xs text-slate-600 mt-0.5">
                                            {verifyResult.total_changes === 0
                                                ? "Everything looks correct ✓"
                                                : `${verifyResult.total_changes} suggestion${verifyResult.total_changes !== 1 ? "s" : ""} — tick the ones you'd like to apply.`}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {verifyResult.total_changes > 0 && (
                                            <button className="hdb-btn hdb-btn-mint" onClick={applyFixes} data-testid="apply-verify-btn">
                                                <CheckCircle2 size={13} /> Apply selected
                                            </button>
                                        )}
                                        <button className="hdb-btn hdb-btn-ghost" onClick={() => setVerifyResult(null)}>Dismiss</button>
                                    </div>
                                </div>
                                {verifyResult.suggestions.filter((s) => (s.changes || []).length > 0).map((s) => (
                                    <div key={s.line_idx} className="mb-2 last:mb-0 bg-white border border-slate-200 rounded-md p-3">
                                        <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                                            <span className="hdb-tag hdb-tag-slate">Row #{s.line_idx + 1}</span>
                                            {s.name}
                                            <span className="text-xs text-slate-400">· {s.confidence}% confidence</span>
                                        </div>
                                        {s.changes.map((c) => {
                                            const key = `${s.line_idx}-${c.field}`;
                                            return (
                                                <label key={key} className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                                                    <input type="checkbox" checked={!!selectedFixes[key]}
                                                           onChange={(e) => setSelectedFixes({ ...selectedFixes, [key]: e.target.checked })}
                                                           data-testid={`fix-${key}`} />
                                                    <span className="hdb-mono text-xs text-slate-500 w-16">{c.field === "tax_percent" ? "GST%" : c.field.toUpperCase()}</span>
                                                    <span className="hdb-mono text-slate-500 line-through">{c.current === "" || c.current === null ? "(empty)" : c.current}</span>
                                                    <span className="text-slate-400">→</span>
                                                    <span className="hdb-mono font-bold text-emerald-700">{c.suggested}</span>
                                                    <span className="text-xs text-slate-400 italic">{c.action === "add" ? "· fill missing" : "· replace"}</span>
                                                </label>
                                            );
                                        })}
                                        {s.note && <div className="text-[11px] text-slate-500 mt-2 italic">{s.note}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="overflow-x-auto">
                            <table className="hdb-table w-full min-w-[980px]">
                                <thead>
                                    <tr>
                                        <th style={{width: 220}}>Item</th>
                                        <th>HSN</th>
                                        <th style={{width: 90}}>Qty</th>
                                        <th style={{width: 90}}>Unit</th>
                                        {invoiceType === "B2C" && <th className="text-right" style={{width: 90}}>MRP</th>}
                                        <th className="text-right" style={{width: 100}}>Rate</th>
                                        <th className="text-right" style={{width: 90}}>GST%</th>
                                        <th className="text-right" style={{width: 110}}>Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        // Build render sequence: category group headers followed by their rows.
                                        const rendered = [];
                                        let lastCat = null;
                                        let displaySl = 0;
                                        sortedIndices.forEach((i) => {
                                            const l = lines[i];
                                            const cat = l.category || "Uncategorized";
                                            if (cat !== lastCat) {
                                                rendered.push(
                                                    <tr key={`cat-${cat}-${i}`} className="bg-slate-50">
                                                        <td colSpan={invoiceType === "B2C" ? 9 : 8}
                                                            className="hdb-heading font-bold text-xs uppercase tracking-widest text-slate-600 px-3 py-1.5"
                                                            data-testid={`row-cat-header-${cat}`}>
                                                            {cat}
                                                        </td>
                                                    </tr>
                                                );
                                                lastCat = cat;
                                            }
                                            displaySl += 1;
                                            const total = (Number(l.qty || 0) * Number(l.rate || 0)) *
                                                          (1 + Number(l.tax_percent || 0) / 100);
                                            const dup = duplicateHints.get(i);
                                            const hsnBusyRow = hsnBusy.has(i);
                                            const rateBusyRow = rateBusy.has(i);
                                            rendered.push(
                                                <tr key={i} className="relative">
                                                    <td>
                                                        <input className="hdb-input" placeholder="Search item…"
                                                               value={l.name}
                                                               onChange={(e) => setLine(i, { name: e.target.value })}
                                                               onFocus={() => { setPickerFor(i); setPickerQ(l.name); }}
                                                               data-testid={`line-name-${i}`} />
                                                        <div className="mt-1">
                                                            <CategoryCombobox
                                                                value={l.category}
                                                                onChange={(v) => setLine(i, { category: v })}
                                                                options={allCats}
                                                                placeholder="Category…"
                                                                compact
                                                                testId={`line-category-${i}`} />
                                                        </div>
                                                        {dup && (
                                                            <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1"
                                                                 data-testid={`dup-warning-${i}`}>
                                                                <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />
                                                                <span>{dup} — proceed only if intentional.</span>
                                                            </div>
                                                        )}
                                                        {l.hsn_auto && (
                                                            <div className="mt-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                                                                HSN/GST auto-fetched · please verify
                                                            </div>
                                                        )}
                                                        {pickerFor === i && (
                                                            <div className="absolute z-40 bg-white border border-slate-200 rounded-md shadow-lg mt-1 w-[420px] max-h-64 overflow-y-auto"
                                                                 data-testid={`item-picker-${i}`}>
                                                                <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
                                                                    <div className="relative">
                                                                        <Search size={13} className="absolute left-2 top-2 text-slate-400" />
                                                                        <input className="hdb-input pl-7 text-sm"
                                                                               autoFocus value={pickerQ}
                                                                               onChange={(e) => setPickerQ(e.target.value)}
                                                                               placeholder={`Search — ${invoiceType} pricing applied`} />
                                                                    </div>
                                                                </div>
                                                                {filteredItems.map((it) => (
                                                                    <button key={it.id} onClick={() => chooseItem(i, it)}
                                                                            className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0"
                                                                            data-testid={`pick-${it.id}`}>
                                                                        <div className="font-medium text-sm">{it.name}</div>
                                                                        <div className="text-xs text-slate-500 flex gap-3 hdb-mono">
                                                                            <span>HSN {it.hsn}</span>
                                                                            <span className="font-bold text-slate-900">₹{fmtNum(priceFor(it, invoiceType))}</span>
                                                                            <span className="text-slate-400">B2B ₹{fmtNum(it.b2b_price)} · B2C ₹{fmtNum(it.b2c_price)}</span>
                                                                            <span>{it.tax_percent}%</span>
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                                {filteredItems.length === 0 && (
                                                                    <div className="p-3 text-sm text-slate-400 text-center">
                                                                        No matches
                                                                    </div>
                                                                )}
                                                                <div className="p-2 border-t border-slate-100 text-right">
                                                                    <button className="hdb-btn hdb-btn-ghost text-xs"
                                                                            onClick={() => setPickerFor(null)}>Close</button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="flex gap-1">
                                                            <input className="hdb-input hdb-mono" value={l.hsn}
                                                                   onChange={(e) => setLine(i, { hsn: e.target.value, hsn_auto: false })}
                                                                   data-testid={`line-hsn-${i}`} />
                                                            <button type="button"
                                                                    className="hdb-btn hdb-btn-ghost px-2"
                                                                    disabled={hsnBusyRow}
                                                                    title="Auto-fetch HSN & GST rate from LLM lookup — please verify before saving"
                                                                    onClick={() => autoFetchHsn(i)}
                                                                    data-testid={`hsn-lookup-${i}`}>
                                                                {hsnBusyRow ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td><input className="hdb-input hdb-mono" type="number" value={l.qty}
                                                               onChange={(e) => setLine(i, { qty: e.target.value })}
                                                               data-testid={`line-qty-${i}`} /></td>
                                                    <td><input className="hdb-input" value={l.unit}
                                                               onChange={(e) => setLine(i, { unit: e.target.value })}
                                                               data-testid={`line-unit-${i}`} /></td>
                                                    {invoiceType === "B2C" && (
                                                        <td><input className="hdb-input hdb-mono text-right" type="number" value={l.mrp || 0}
                                                                   onChange={(e) => setLine(i, { mrp: e.target.value })}
                                                                   data-testid={`line-mrp-${i}`} /></td>
                                                    )}
                                                    <td><input className="hdb-input hdb-mono text-right" type="number" value={l.rate}
                                                               onChange={(e) => setLine(i, { rate: e.target.value })}
                                                               data-testid={`line-rate-${i}`} />
                                                        <MasterRateHint rate={l.rate} master={masterRateOf(l)} idx={i} onApply={(r) => setLine(i, { rate: r })}/></td>
                                                    <td><input className="hdb-input hdb-mono text-right" type="number" value={l.tax_percent}
                                                               onChange={(e) => setLine(i, { tax_percent: e.target.value })}
                                                               data-testid={`line-gst-${i}`} /></td>
                                                    <td className="hdb-num hdb-num-right font-semibold">{fmtNum(total)}</td>
                                                    <td className="text-right whitespace-nowrap">
                                                        {l.item_id && (
                                                            <button type="button"
                                                                    className="hdb-btn hdb-btn-ghost px-2 mr-1"
                                                                    disabled={rateBusyRow}
                                                                    title="Push this rate into the Item Master for future invoices"
                                                                    onClick={() => updateMasterRate(i)}
                                                                    data-testid={`update-rate-${i}`}>
                                                                {rateBusyRow ? <Loader2 size={12} className="animate-spin" /> : <ArrowUpToLine size={12} />}
                                                            </button>
                                                        )}
                                                        <button className="hdb-btn hdb-btn-danger" onClick={() => rmLine(i)}
                                                                data-testid={`rm-line-${i}`}>
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        });
                                        return rendered;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Totals Sidebar */}
                <div className="space-y-5">
                    <div className="hdb-card p-5 sticky top-4">
                        <h3 className="hdb-heading font-bold text-lg text-slate-900 mb-4">Summary</h3>
                        <div className="mb-4">
                            <label className="hdb-label">Delivery / Transport (₹)</label>
                            <input className="hdb-input hdb-mono" type="number" value={delivery}
                                   onChange={(e) => setDelivery(e.target.value)} data-testid="delivery-input" />
                        </div>
                        {invoiceType === "B2C" && (
                            <div className="mt-4 pt-4 border-t border-slate-200">
                                <div className="hdb-label mb-2">Your Previous Savings (₹) <span className="text-slate-400 normal-case">— editable</span></div>
                                <input className="hdb-input hdb-mono" type="number" value={previousSavings}
                                       placeholder={String(previousSavingsAuto)}
                                       onChange={(e) => setPreviousSavings(e.target.value)}
                                       data-testid="previous-savings-input" />
                                <div className="text-[10px] text-slate-500 mt-1">
                                    Auto-fetched from prior B2C invoices for this buyer ({fmt(previousSavingsAuto)}). Override if needed.
                                </div>
                            </div>
                        )}
                        {preview ? (
                            <div className="space-y-2 text-sm" data-testid="preview-panel">
                                <Row k="Subtotal" v={fmt(preview.subtotal)} />
                                {preview.is_interstate ? (
                                    <Row k="IGST" v={fmt(preview.igst)} />
                                ) : (
                                    <>
                                        <Row k="CGST" v={fmt(preview.cgst)} />
                                        <Row k="SGST" v={fmt(preview.sgst)} />
                                    </>
                                )}
                                {preview.delivery > 0 && <Row k="Delivery" v={fmt(preview.delivery)} />}
                                {Math.abs(preview.round_off || 0) >= 0.01 && (
                                    <Row k="Round Off"
                                         v={(preview.round_off > 0 ? "+ " : "− ") + fmtNum(Math.abs(preview.round_off))} />
                                )}
                                <div className="border-t border-slate-200 pt-3 mt-3 flex justify-between">
                                    <div className="hdb-heading font-bold text-slate-900">Grand Total</div>
                                    <div className="hdb-heading font-black text-slate-900 text-xl hdb-num" data-testid="grand-total">
                                        {fmtInt(preview.grand_total)}
                                    </div>
                                </div>
                                <div className="pt-3 border-t border-slate-100">
                                    {invoiceType === "B2C" ? (
                                        <div data-testid="savings-summary">
                                            <div className="hdb-label mb-2">Savings Summary</div>
                                            <div className="flex justify-between text-xs py-1 text-slate-600">
                                                <span>Your Previous Savings</span>
                                                <span className="hdb-num">{fmt(preview.previous_savings || 0)}</span>
                                            </div>
                                            <div className="flex justify-between text-xs py-1 text-slate-600">
                                                <span>Your Saving Today</span>
                                                <span className="hdb-num">{fmt(preview.today_savings || 0)}</span>
                                            </div>
                                            <div className="mt-2 -mx-1 px-3 py-2 rounded-md bg-emerald-500 text-white flex justify-between items-center">
                                                <span className="hdb-heading text-[11px] font-black uppercase tracking-wide">Total Savings Till Now</span>
                                                <span className="hdb-num font-black" data-testid="total-savings">{fmt(preview.total_savings || 0)}</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="hdb-label mb-2">Tax by rate</div>
                                            {preview.tax_summary.map((t) => (
                                                <div key={t.rate} className="flex justify-between text-xs py-1 hdb-num text-slate-600">
                                                    <span>@ {t.rate}% on {fmtNum(t.taxable)}</span>
                                                    <span>{fmtNum(t.total_tax)}</span>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-slate-400">Add line items to see totals</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between text-slate-700">
            <span>{k}</span>
            <span className="hdb-num font-semibold">{v}</span>
        </div>
    );
}


// ↑/↓ under the rate whenever today's item-master rate differs from the line's rate.
function MasterRateHint({ rate, master, idx, onApply }) {
    const m = master;
    const r = Number(rate) || 0;
    if (m == null) return null;
    if (m === 0) return <div className="text-[10px] font-bold text-amber-700 mt-0.5" data-testid={`line-master-unpriced-${idx}`}>not priced in master</div>;
    if (Math.abs(m - r) < 0.005) return null;
    const up = m > r;
    return (
        <button type="button" onClick={() => onApply(m)} title="Use today's item-master rate"
                className={`text-[10px] font-bold mt-0.5 w-full text-right ${up ? "text-red-600" : "text-emerald-700"}`}
                data-testid={`line-master-${up ? "up" : "down"}-${idx}`}>
            {up ? "↑" : "↓"} master ₹{m.toFixed(2)}
        </button>
    );
}
